/**
 * Core screen recording functionality using MediaRecorder API
 */

export interface RecordingOptions {
    screen: boolean
    audio: boolean        // system audio
    microphone: boolean   // mic capture
    camera: boolean       // camera capture
    micDeviceId?: string
    cameraDeviceId?: string
}

export interface RecordingResult {
    blob: Blob
    cameraBlob?: Blob     // Camera recording blob
    duration: number
    frameRate: number
}

class ScreenRecorder {
    private mediaRecorder: MediaRecorder | null = null
    private cameraMediaRecorder: MediaRecorder | null = null
    private recordedChunks: Blob[] = []
    private cameraChunks: Blob[] = []
    private stream: MediaStream | null = null
    private micStream: MediaStream | null = null
    private cameraStream: MediaStream | null = null
    private startTime: number = 0

    /**
     * Request permissions and set up streams
     */
    async requestPermissions(options: RecordingOptions): Promise<MediaStream> {
        const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)

        // Firefox handles displaySurface and audio constraints strictly; fallback if primary fails
        let screenStream: MediaStream
        try {
            const constraints: DisplayMediaStreamOptions = {
                video: isFirefox ? true : { displaySurface: 'monitor', cursor: 'never' } as any,
                audio: options.audio,
            }
            screenStream = await navigator.mediaDevices.getDisplayMedia(constraints)
        } catch (err: any) {
            console.warn('[ScreenRecorder] Initial getDisplayMedia failed, retrying with flexible constraints:', err)
            // Fallback for Firefox/strict browsers if displaySurface or audio constraints rejected
            try {
                screenStream = await navigator.mediaDevices.getDisplayMedia({
                    video: { cursor: 'never' } as any,
                    audio: options.audio ? true : false,
                })
            } catch (fallbackErr) {
                // Final fallback: video only
                screenStream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: 'never' } as any })
            }
        }
        this.stream = screenStream

        // If microphone is requested, get mic stream and combine
        if (options.microphone) {
            try {
                this.micStream = await navigator.mediaDevices.getUserMedia({
                    audio: options.micDeviceId ? { deviceId: { exact: options.micDeviceId } } : true
                })

                // Combine streams
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
                if (audioContext.state === 'suspended') {
                    await audioContext.resume()
                }

                const destination = audioContext.createMediaStreamDestination()

                // Add screen audio if available and live
                const screenAudioTracks = screenStream.getAudioTracks()
                if (screenAudioTracks.length > 0 && screenAudioTracks[0].readyState === 'live') {
                    const screenSource = audioContext.createMediaStreamSource(
                        new MediaStream([screenAudioTracks[0]])
                    )
                    screenSource.connect(destination)
                }

                // Add mic audio if available and live
                const micAudioTracks = this.micStream.getAudioTracks()
                if (micAudioTracks.length > 0 && micAudioTracks[0].readyState === 'live') {
                    const micSource = audioContext.createMediaStreamSource(this.micStream)
                    micSource.connect(destination)
                }

                // Create combined stream with video tracks + mixed audio tracks
                const combinedTracks: MediaStreamTrack[] = [
                    ...screenStream.getVideoTracks()
                ]

                const mixedAudio = destination.stream.getAudioTracks()
                if (mixedAudio.length > 0) {
                    combinedTracks.push(...mixedAudio)
                } else if (screenAudioTracks.length > 0) {
                    combinedTracks.push(...screenAudioTracks)
                }

                this.stream = new MediaStream(combinedTracks)
            } catch (err) {
                console.warn('Could not get microphone access:', err)
            }
        }

        // If camera is requested, get camera stream
        if (options.camera) {
            try {
                this.cameraStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        deviceId: options.cameraDeviceId ? { exact: options.cameraDeviceId } : undefined,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 }
                    }
                })
            } catch (err) {
                console.warn('Could not get camera access:', err)
            }
        }

        return this.stream
    }

    /**
     * Start recording
     */
    startRecording(onDataAvailable?: (chunk: Blob) => void): void {
        if (!this.stream) {
            throw new Error('No media stream available. Call requestPermissions first.')
        }

        this.recordedChunks = []
        this.cameraChunks = []
        this.startTime = Date.now()

        const mimeType = this.getSupportedMimeType()

        // Screen Recorder
        this.mediaRecorder = new MediaRecorder(this.stream, {
            mimeType,
            videoBitsPerSecond: 2500000,
        })

        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.recordedChunks.push(event.data)
                onDataAvailable?.(event.data)
            }
        }

        // Camera Recorder (if available)
        if (this.cameraStream) {
            this.cameraMediaRecorder = new MediaRecorder(this.cameraStream, {
                mimeType,
                videoBitsPerSecond: 1000000, // 1 Mbps is enough for camera
            })

            this.cameraMediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.cameraChunks.push(event.data)
                }
            }
            this.cameraMediaRecorder.start(1000)
        }

        this.mediaRecorder.start(1000)
    }

    /**
     * Pause recording
     */
    pauseRecording(): void {
        if (this.mediaRecorder?.state === 'recording') {
            this.mediaRecorder.pause()
        }
        if (this.cameraMediaRecorder?.state === 'recording') {
            this.cameraMediaRecorder.pause()
        }
    }

    /**
     * Resume recording
     */
    resumeRecording(): void {
        if (this.mediaRecorder?.state === 'paused') {
            this.mediaRecorder.resume()
        }
        if (this.cameraMediaRecorder?.state === 'paused') {
            this.cameraMediaRecorder.resume()
        }
    }

    /**
     * Stop all active streams and tracks
     */
    stopAllStreams(): void {
        this.stream?.getTracks().forEach(track => track.stop())
        this.micStream?.getTracks().forEach(track => track.stop())
        this.cameraStream?.getTracks().forEach(track => track.stop())

        this.stream = null
        this.micStream = null
        this.cameraStream = null
        this.mediaRecorder = null
        this.cameraMediaRecorder = null
    }

    /**
     * Stop recording and return the result
     */
    async stopRecording(): Promise<RecordingResult> {
        if (!this.mediaRecorder) {
            throw new Error('No recording in progress')
        }

        return new Promise((resolve) => {
            let mainStopped = false
            let cameraStopped = !this.cameraMediaRecorder

            const checkAndResolve = () => {
                if (mainStopped && cameraStopped) {
                    const duration = (Date.now() - this.startTime) / 1000
                    const mimeType = this.getSupportedMimeType()

                    const blob = new Blob(this.recordedChunks, { type: mimeType })
                    const cameraBlob = this.cameraChunks.length > 0
                        ? new Blob(this.cameraChunks, { type: mimeType })
                        : undefined

                    const videoTrack = this.stream ? this.stream.getVideoTracks()[0] : null
                    const frameRate = videoTrack ? videoTrack.getSettings().frameRate || 30 : 30

                    // The actual streams are already stopped via stopAllStreams()
                    // being called outside the promise or at the start of stop

                    resolve({ blob, cameraBlob, duration, frameRate })
                }
            }

            if (this.mediaRecorder) {
                this.mediaRecorder.onstop = () => {
                    mainStopped = true
                    checkAndResolve()
                }
            }

            if (this.cameraMediaRecorder) {
                this.cameraMediaRecorder.onstop = () => {
                    cameraStopped = true
                    checkAndResolve()
                }
                this.cameraMediaRecorder.stop()
            }

            if (this.mediaRecorder) {
                this.mediaRecorder.stop()
            }

            // Kill tracks immediately so the LED goes off now, not after processing
            const screenTrack = this.stream?.getTracks()
            const micTrack = this.micStream?.getTracks()
            const camTrack = this.cameraStream?.getTracks()

            // We don't call stopAllStreams() yet because checkAndResolve needs the 
            // reference to recordedChunks, but we DO stop the hardware tracks.
            screenTrack?.forEach(t => t.stop())
            micTrack?.forEach(t => t.stop())
            camTrack?.forEach(t => t.stop())
        })
    }

    /**
     * Check if recording is in progress
     */
    isRecording(): boolean {
        return this.mediaRecorder?.state === 'recording'
    }

    /**
     * Check if recording is paused
     */
    isPaused(): boolean {
        return this.mediaRecorder?.state === 'paused'
    }

    /**
     * Toggle microphone
     */
    toggleMic(enabled: boolean): void {
        if (this.micStream) {
            this.micStream.getAudioTracks().forEach(track => {
                track.enabled = enabled
            })
        }
    }

    /**
     * Get the current stream
     */
    getStream(): MediaStream | null {
        return this.stream
    }

    /**
     * Get the camera stream
     */
    getCameraStream(): MediaStream | null {
        return this.cameraStream
    }

    /**
     * Get a supported MIME type for recording
     */
    private getSupportedMimeType(): string {
        const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)
        
        // Firefox performs significantly smoother with vp8 software/hardware encoding than vp9
        const types = isFirefox
            ? [
                'video/webm;codecs=vp8,opus',
                'video/webm;codecs=vp9,opus',
                'video/webm',
            ]
            : [
                'video/webm;codecs=vp9,opus',
                'video/webm;codecs=vp8,opus',
                'video/webm',
                'video/mp4',
            ]

        for (const type of types) {
            if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
                return type
            }
        }

        return 'video/webm'
    }
}

// Singleton instance
export const screenRecorder = new ScreenRecorder()
